import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storageSession from "redux-persist/lib/storage/session";
import { InitializeToken } from "../../models/authentication/initializeToken";
import { LogOutIdentResponse } from "../../models/authentication/logOutIdentResponse";
import { RefreshTokenResponse } from "../../models/authentication/refreshTokenResponse";
import { getPayloadFromAccessToken } from "../../services/auth/jwtTransform";
import { authenticationApi } from "../api/authenticationApi";
import { RootState } from "../stores/store";

type SliceState = {
  initializeToken: InitializeToken | null;
  isAuthenticated: boolean;
  tokenRefreshRequired: boolean;
  logOutRequired: boolean;
  redirectUrl: string;
  redirectRequired: boolean;
};

const initialState: SliceState = {
  initializeToken: null,
  isAuthenticated: false,
  tokenRefreshRequired: false,
  logOutRequired: false,
  redirectRequired: false,
  redirectUrl: "",
};

const slice = createSlice({
  name: "authSlice",
  initialState,
  reducers: {
    logout: (state, { payload }: PayloadAction<{ redirectUrl: string }>) => {
      const { redirectUrl } = payload;
      if (redirectUrl) {
        state.redirectUrl = redirectUrl;
        if (state.initializeToken === null) {
          state.redirectRequired = true;
          return;
        }
      }
      state.logOutRequired = true;
    },
    login: (state, { payload }: PayloadAction<InitializeToken>) => {
      state.logOutRequired = false;
      state.tokenRefreshRequired = false;
      state.redirectUrl = "";
      state.isAuthenticated = true;
      state.initializeToken = { ...payload };
    },
    setTokenRefreshRequired: (state, { payload }: PayloadAction<boolean>) => {
      state.tokenRefreshRequired = payload;
    },
    setRedirectRequired: (state, { payload }: PayloadAction<boolean>) => {
      state.redirectRequired = payload;
    },
    setRefreshToken: (state, { payload }: PayloadAction<RefreshTokenResponse>) => {
      const { access_token } = payload;
      if (state.initializeToken) {
        state.initializeToken = {
          ...state.initializeToken,
          access_token,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        authenticationApi.endpoints.logOutIdent.matchFulfilled,
        authenticationApi.endpoints.logOutIdent.matchRejected
      ),
      (state, action) => {
        state.initializeToken = initialState.initializeToken;
        state.logOutRequired = false;
        state.tokenRefreshRequired = false;
        state.isAuthenticated = false;

        if (action.payload) {
          const url = (action.payload as LogOutIdentResponse)?.redirectUrl;
          if (url) {
            state.redirectUrl = url;
          }
        }
        state.redirectRequired = true;
      }
    );
  },
});

export const { logout, login, setTokenRefreshRequired, setRedirectRequired, setRefreshToken } = slice.actions;

const authPersistConfig = {
  key: "auth",
  storage: storageSession,
};

export const authPersistedReducer = persistReducer(authPersistConfig, slice.reducer);

export const selectIsAuthenticated = (state: RootState) => state.authSlice.isAuthenticated;

export const selectIsInitializeToken = (state: RootState) => state.authSlice.initializeToken !== null;

export const selectIdentifier = (state: RootState) => {
  const payload = getPayloadFromAccessToken(state.authSlice.initializeToken?.access_token);
  return payload ? payload.eIdentifier : "";
};

export const selectAccessToken = (state: RootState) => {
  return getPayloadFromAccessToken(state.authSlice.initializeToken?.access_token);
};

export const selectInitializeToken = (state: RootState) => state.authSlice.initializeToken;

export const selectLogOutRequired = (state: RootState) => state.authSlice.logOutRequired;

export const selectRedirectRequired = (state: RootState) => state.authSlice.redirectRequired;

export const selectIsCaseWorker = (state: RootState) => state.authSlice.initializeToken?.caseworker ?? false;

export const selectIsReadOnlyAccess = (state: RootState) => state.authSlice.initializeToken?.read_only ?? false;

export const selectRedirectUrl = (state: RootState) => state.authSlice.redirectUrl;
